import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import Breadcrumbs from "../../components/Breadcrumbs";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import HardwareProductForm from "../../components/hardwareCatalogue/HardwareProductForm";
import { toHardwareProductPayload, validateImageFile } from "../../components/hardwareCatalogue/hardwareProductPayload";
import { useAuth } from "../../providers/AuthContext";
import {
  getHardwareProduct,
  updateHardwareProduct,
  uploadHardwareProductImage,
  deleteHardwareProductImage,
} from "../../api/HardwareProducts";

const EditHardwareProductPage = () => {
  const { hardwareProductPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", confirm: () => {} });

  // An image change is saved on its own and bumps the product's stamp, so the fields the
  // server returns are merged in without discarding what the admin is still editing.
  const applyImageResult = (updated) => {
    setProduct((current) => ({
      ...current,
      imageDisplayUrl: updated?.imageDisplayUrl ?? null,
      hasUploadedImage: !!updated?.hasUploadedImage,
      updatedBy: updated?.updatedBy ?? current?.updatedBy,
      updatedDateTimeUTC: updated?.updatedDateTimeUTC ?? current?.updatedDateTimeUTC,
    }));
  };

  const handleImageSelected = async (file) => {
    const problem = validateImageFile(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    setImageBusy(true);
    try {
      const accessToken = await getAccessToken();
      applyImageResult(await uploadHardwareProductImage(hardwareProductPublicId, file, accessToken));
      toast.success("Image uploaded", { iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" } });
    } catch (error) {
      toast.error(error.message || "Failed to upload the image");
    } finally {
      setImageBusy(false);
    }
  };

  const handleImageRemove = () => {
    setConfirmState({
      isOpen: true,
      title: "Remove the uploaded image?",
      confirm: async () => {
        setImageBusy(true);
        try {
          const accessToken = await getAccessToken();
          applyImageResult(await deleteHardwareProductImage(hardwareProductPublicId, accessToken));
          toast.success("Image removed", { iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" } });
        } catch (error) {
          toast.error(error.message || "Failed to remove the image");
        } finally {
          setImageBusy(false);
        }
      },
    });
  };

  const loadProduct = useCallback(async () => {
    setLoading(true);
    try {
      const accessToken = await getAccessToken();
      setProduct(await getHardwareProduct(hardwareProductPublicId, accessToken));
    } catch (error) {
      toast.error(error.message || "Failed to load product");
    } finally {
      setLoading(false);
    }
  }, [hardwareProductPublicId, getAccessToken]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const handleBackClick = () => {
    if (!hasChanges) return navigate(-1);
    setConfirmState({ isOpen: true, title: "Discard changes?", confirm: () => navigate(-1) });
  };

  const handleSave = async () => {
    const payload = toHardwareProductPayload(product);
    if (!payload.name) {
      toast.error("Name is required");
      return;
    }

    setIsSaving(true);
    try {
      const accessToken = await getAccessToken();
      // updatedDateTimeUTC is the value we loaded: the server refuses to overwrite a newer change.
      await updateHardwareProduct(
        { ...payload, hardwareProductPublicId, updatedDateTimeUTC: product?.updatedDateTimeUTC ?? null },
        accessToken
      );
      // Reload rather than trusting the response, so the next save echoes the stamp as stored.
      await loadProduct();
      setHasChanges(false);
      toast.success("Product updated", { iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" } });
    } catch (error) {
      toast.error(error.message || "Failed to update product");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage title={"Edit product"} loading={loading} />
          </div>
          <div className={`${loading ? "loading" : ""} page-content`}>
            <Breadcrumbs />
            <div className="flex items-center gap-4 mb-4">
              <button className="text-xl" onClick={handleBackClick} aria-label="Back">
                <FontAwesomeIcon icon={faArrowLeftLong} />
              </button>
              <h2 className="border-l border-mute-200 pl-4">{`Edit hardware product: ${product?.name || ""}`}</h2>
              <div className="flex gap-4 ml-auto">
                <button className="btn btn-outline-primary" onClick={handleBackClick}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={!hasChanges || isSaving || imageBusy || !product}>Save</button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              {product && (
                <HardwareProductForm
                  product={product}
                  setProduct={setProduct}
                  setHasChanges={setHasChanges}
                  onImageSelected={handleImageSelected}
                  onImageRemove={handleImageRemove}
                  imageBusy={imageBusy || isSaving}
                />
              )}
            </div>
          </div>
        </main>
      </div>
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default EditHardwareProductPage;
